import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const BookDesign = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Professional Cover Design',
      description: 'Eye-catching, genre-appropriate covers that make your book stand out in any marketplace.',
      features: ['Custom illustration', '3D mockups', 'Multiple concepts', 'Unlimited revisions']
    },
    {
      title: 'Interior Formatting',
      description: 'Professional layout and typography for print and eBook that meets all retailer specifications.',
      features: ['Print formatting', 'eBook formatting', 'Chapter styling', 'Professional typography']
    },
    {
      title: 'Print-on-Demand Setup',
      description: 'Complete setup for KDP Print, IngramSpark, and other print-on-demand services.',
      features: ['KDP setup', 'IngramSpark setup', 'File preparation', 'Proof review']
    },
    {
      title: 'Brand Identity',
      description: 'Consistent branding across all your books and author materials for series and multi-book authors.',
      features: ['Series design', 'Author branding', 'Marketing materials', 'Style guides']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Design Brief & Concept',
      description: 'We discuss your vision, genre expectations, and target audience. Our designers create 3-5 initial cover concepts based on market research and your preferences.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'Refinement & Interior Layout',
      description: 'You select your preferred concept and we refine it to perfection. Simultaneously, we format your interior with professional typography and layout.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Finalization & Print Setup',
      description: 'Final files delivered in all formats needed. We set up your print-on-demand accounts and upload files, providing proof copies for final approval.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png',
      title: 'Award-Winning Designers',
      description: 'Our design team has won multiple book cover design awards across all genres.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Market Research',
      description: 'Every design is informed by current market trends and genre-specific expectations.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Fast Delivery',
      description: 'Most projects completed within 2-3 weeks from brief to final files.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Unlimited Revisions',
      description: 'We work until you love your cover. Revisions included in all packages.'
    }
  ];

  const faqs = [
    {
      question: 'How much does book design and printing cost?',
      answer: 'Cover design: $500-1,500 depending on complexity. Interior formatting: $150-400 for standard novels. Print setup: $100-200 per platform. We offer package deals combining cover, interior, and print setup. Print-on-demand has no upfront printing costs - you only pay for books you order.'
    },
    {
      question: 'What\'s the difference between eBook and print formatting?',
      answer: 'eBook formatting uses reflowable text that adapts to different screen sizes and devices. Print formatting uses fixed layouts with specific page dimensions, margins, and page numbers. Most books need both formats. We provide both in our packages.'
    },
    {
      question: 'Do I own the rights to my cover design?',
      answer: 'Yes, absolutely. You receive full commercial rights to use your cover design. We provide high-resolution files for print, web-optimized files for marketing, and 3D mockups for promotional use.'
    },
    {
      question: 'What if I don\'t like the initial cover concepts?',
      answer: 'We provide 3-5 initial concepts based on your brief. If none resonate, we\'ll discuss what\'s not working and create new concepts at no additional charge. Our goal is a cover you absolutely love.'
    },
    {
      question: 'Can you match my existing series covers?',
      answer: 'Yes! We specialize in series design. We can match your existing covers or redesign your entire series for consistent branding. Provide your current covers and we\'ll match style, fonts, and layout.'
    },
    {
      question: 'What print-on-demand services do you recommend?',
      answer: 'For US authors: KDP Print (Amazon) for wide distribution and IngramSpark for bookstore/library distribution. For international: KDP Print globally. We can set up both and provide files formatted for each platform\'s specifications.'
    },
    {
      question: 'How long does the design process take?',
      answer: 'Cover design: 2-3 weeks (1 week for concepts, 1-2 weeks for revisions). Interior formatting: 1 week for novels, 2-3 weeks for complex layouts with images. Print setup: 3-5 days. Rush options available.'
    },
    {
      question: 'What file formats will I receive?',
      answer: 'Print-ready PDFs for KDP and IngramSpark, eBook files (EPUB, MOBI if needed), high-res JPG covers for marketing, 3D mockups, and source files. Everything you need for publishing and promotion.'
    }
  ];

  const testimonials = [
    {
      name: 'Elena Rodriguez',
      title: 'Romance Series Author',
      text: 'The cover designs for my series are stunning! They perfectly capture the tone and now my books look professional alongside traditionally published titles.',
      rating: 5
    },
    {
      name: 'Kevin Murphy',
      title: 'Thriller Author',
      text: 'My previous self-designed covers weren\'t selling. After getting professional covers, my sales tripled in the first month. Worth every penny.',
      rating: 5
    },
    {
      name: 'Samantha Lee',
      title: 'Non-Fiction Author',
      text: 'The interior formatting is flawless. My book looks as good inside as traditional publishers. The print quality from KDP is excellent.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL BOOK DESIGN & PRINTING</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Stunning Book Design That <span className="text-amber-700">Sells</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Award-winning cover design, professional interior formatting, and hassle-free print-on-demand setup. Make your book look as good as traditionally published titles.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Design Quote
                </Button>
                <Button 
                  onClick={() => scrollToSection('process')}
                  className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  See How It Works
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">1,500+</div>
                  <div className="text-gray-600 text-sm">Covers Designed</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">15+</div>
                  <div className="text-gray-600 text-sm">Design Awards</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">100%</div>
                  <div className="text-gray-600 text-sm">Rights Included</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">2-3</div>
                  <div className="text-gray-600 text-sm">Weeks Average</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1535223289827-42f1e9919769?w=800&h=600&fit=crop" 
                  alt="Professional book design"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img 
                        src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                        alt="Covers designed"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">1,500+</div>
                      <div className="text-sm text-gray-600">Covers Designed</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 right-12 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Cover Design Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop" 
                alt="Award-winning cover designs"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-700/20 to-transparent rounded-2xl"></div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">COVER DESIGN</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Your Cover Sells <span className="text-amber-700">Your Book</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Readers judge books by their covers. Our award-winning designers create covers that catch eyes, communicate genre, and convert browsers into buyers.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                      alt="Market research"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Market-Driven Design</h3>
                    <p className="text-gray-600">Every cover is researched against current bestsellers in your genre and category.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/2521/2521826.png" 
                      alt="Multiple concepts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Multiple Concepts</h3>
                    <p className="text-gray-600">Receive 3-5 professional cover concepts to choose from, not just one take-it-or-leave-it design.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/1087/1087927.png" 
                      alt="3D mockups"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3D Mockups & Marketing Files</h3>
                    <p className="text-gray-600">Receive realistic 3D book mockups and marketing files perfect for social media promotion.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                See Cover Portfolio
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              First Impressions Matter
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Your cover is your book's first and most important marketing tool. Professional design pays for itself many times over in increased sales.
            </p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete <span className="text-amber-700">Design Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need from cover design to printed books in readers' hands.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="text-amber-700">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A collaborative design process that ensures you love your book's final appearance.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" style={{ top: '80px' }}></div>

              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>

                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>

                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute -right-4 top-20 text-amber-500">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {index < process.length - 1 && (
                    <div className="md:hidden flex justify-center py-4">
                      <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l3.293-3.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">Ready to design your book?</p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-12 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Get Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-amber-700">Rushmore Publishing</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Award-winning designs informed by market research, with unlimited revisions until you're completely satisfied.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&h=600&fit=crop" 
                alt="Professional design team"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold mb-2">15+</div>
                <div className="text-sm">Design Awards Won</div>
                <div className="mt-3 text-xs opacity-90">Industry recognition for excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1920&h=800&fit=crop" 
            alt="Start your design"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">FREE DESIGN CONSULTATION</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Design Your Book?
            </h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">
              Get a free quote and see sample covers in your genre. Let's create a design that makes your book irresistible to readers.
            </p>
            
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
            >
              Get Your Free Quote
            </Button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20 mt-12">
              <div>
                <div className="text-3xl font-bold mb-2">1,500+</div>
                <div className="text-sm opacity-80">Covers Designed</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">15+</div>
                <div className="text-sm opacity-80">Design Awards</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm opacity-80">Rights Included</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Authors Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-amber-700">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all"
                >
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookDesign;